import { zonedNow } from '../lib/dates';
import {
  BoardControls,
  ResizableBoard,
  readBoard,
  readNumber,
  MIN_ZOOM,
  MAX_ZOOM,
  type BoardSize,
} from '../components/ResizableBoard';

const ZOOM_KEY = 'td.calendar.zoom';
const BOARD_KEY = 'td.calendar.board';
import { useEffect, useMemo, useState } from 'react';
import {
  CALENDAR_VIEWS,
  eventColor,
  addDays,
  addMonths,
  daysInMonth,
  minutesToLabel,
  minutesRangeLabel,
  holidaysInRange,
  districtEventsInRange,
  DISTRICT_NAME,
  parseDateKey,
  toDateKey,
  type CalendarView,
  type CategoryDef,
  type DistrictMark,
  type EventInstance,
  type HolidayMark,
} from '@td/shared';
import { useEvents } from '../hooks/useEvents';
import { useCategories } from '../hooks/useCategories';
import { EventEditor } from '../components/EventEditor';
import { CategoryManager } from '../components/CategoryManager';
import { HeaderMenu, MenuSection, MenuToggle } from '../components/HeaderMenu';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Sunday-first index, matching how US school calendars are laid out.
 * (The to-do list stays Monday-first — a school *week* is Mon-Fri, which is a
 * different thing from how a month grid is drawn.)
 */
function dowIndex(date: Date): number {
  return date.getDay();
}

function rangeFor(view: CalendarView, anchor: string): { from: string; to: string } {
  const d = parseDateKey(anchor);
  if (view === 'day') return { from: anchor, to: anchor };
  if (view === 'week') {
    const from = addDays(anchor, -dowIndex(d));
    return { from, to: addDays(from, 6) };
  }
  const first = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  const last = addDays(first, daysInMonth(d.getFullYear(), d.getMonth() + 1) - 1);
  // Pad to whole weeks so the month grid has no ragged edges.
  return {
    from: addDays(first, -dowIndex(parseDateKey(first))),
    to: addDays(last, 6 - dowIndex(parseDateKey(last))),
  };
}

function EventChip({
  e,
  categories,
  onClick,
}: {
  e: EventInstance;
  categories: CategoryDef[];
  onClick: () => void;
}) {
  const time = e.allDay ? 'All day' : minutesRangeLabel(e.startMinutes, e.endMinutes);
  return (
    <button
      // Stop the click reaching the day cell, which would open a new event.
      onClick={(ev) => {
        ev.stopPropagation();
        onClick();
      }}
      className="block w-full rounded border-l-[3px] bg-white px-1.5 py-0.5 text-left text-[11px] leading-tight text-espresso hover:bg-linen"
      style={{ borderLeftColor: eventColor(e, categories) }}
      title={`${e.title} · ${time}`}
    >
      {!e.allDay && <span className="block text-[10px] text-stone">{time}</span>}
      <span className="block truncate">{e.title}</span>
    </button>
  );
}

/** District dates come from the published ECS calendar — read-only. */
function DistrictChip({ d }: { d: DistrictMark }) {
  return (
    <div
      className="truncate rounded px-1.5 py-0.5 text-[10px] leading-tight"
      style={{ backgroundColor: `${d.color}22`, color: d.color }}
      title={`${DISTRICT_NAME}: ${d.name}`}
    >
      {d.name}
    </div>
  );
}

/** Holidays are computed, not stored — read-only, and visually quieter. */
function HolidayChip({ h }: { h: HolidayMark }) {
  return (
    <div
      className="truncate rounded bg-rose/10 px-1.5 py-0.5 text-[10px] leading-tight text-rust"
      title={h.name}
    >
      {h.name}
    </div>
  );
}

export function CalendarPage() {
  const today = toDateKey(zonedNow());
  const [view, setView] = useState<CalendarView>('month');
  const [anchor, setAnchor] = useState(today);
  const [editing, setEditing] = useState<EventInstance | null>(null);
  const [creatingOn, setCreatingOn] = useState<string | null>(null);
  const [managingCategories, setManagingCategories] = useState(false);
  const [showHolidays, setShowHolidays] = useState(true);
  const [showDistrict, setShowDistrict] = useState(true);
  // `all` includes hidden built-ins, so an event still pointing at one can
  // resolve its colour while the reassignment settles.
  const { all: allCategories } = useCategories();

  const { from, to } = useMemo(() => rangeFor(view, anchor), [view, anchor]);
  const { data: events, isLoading } = useEvents(from, to);

  // Computed locally — no request, and it works for any year.
  const holidaysByDate = useMemo(() => {
    if (!showHolidays) return new Map<string, HolidayMark[]>();
    const map = new Map<string, HolidayMark[]>();
    for (const h of holidaysInRange(from, to)) {
      const list = map.get(h.date);
      if (list) list.push(h);
      else map.set(h.date, [h]);
    }
    return map;
  }, [from, to, showHolidays]);

  const districtByDate = useMemo(() => {
    if (!showDistrict) return new Map<string, DistrictMark[]>();
    const map = new Map<string, DistrictMark[]>();
    for (const d of districtEventsInRange(from, to)) {
      const list = map.get(d.date);
      if (list) list.push(d);
      else map.set(d.date, [d]);
    }
    return map;
  }, [from, to, showDistrict]);

  const byDate = useMemo(() => {
    const map = new Map<string, EventInstance[]>();
    for (const e of events ?? []) {
      const list = map.get(e.date);
      if (list) list.push(e);
      else map.set(e.date, [e]);
    }
    return map;
  }, [events]);

  // D / W / M switch views, arrows page through.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return;
      const k = e.key.toLowerCase();
      if (k === 'd') setView('day');
      if (k === 'w') setView('week');
      if (k === 'm') setView('month');
      if (k === 't') setAnchor(today);
      if (e.key === 'ArrowLeft') setAnchor((a) => step(a, -1));
      if (e.key === 'ArrowRight') setAnchor((a) => step(a, 1));
    }
    function step(a: string, dir: number) {
      return view === 'day'
        ? addDays(a, dir)
        : view === 'week'
          ? addDays(a, dir * 7)
          : addMonths(a, dir);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, today]);

  function move(dir: number) {
    setAnchor(
      view === 'day'
        ? addDays(anchor, dir)
        : view === 'week'
          ? addDays(anchor, dir * 7)
          : addMonths(anchor, dir),
    );
  }

  const anchorDate = parseDateKey(anchor);
  const heading =
    view === 'month'
      ? anchorDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
      : view === 'week'
        ? `${parseDateKey(from).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${parseDateKey(to).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
        : anchorDate.toLocaleDateString(undefined, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          });

  const days: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) days.push(d);

  /*
   * Size and zoom, remembered on this device — the same controls the lesson
   * plan has, from the same component, so the two cannot drift apart.
   */
  const [zoom, setZoom] = useState(() => readNumber(ZOOM_KEY, 1, MIN_ZOOM, MAX_ZOOM));
  const [board, setBoard] = useState<BoardSize>(() => readBoard(BOARD_KEY));

  useEffect(() => {
    try {
      localStorage.setItem(ZOOM_KEY, String(zoom));
      localStorage.setItem(BOARD_KEY, JSON.stringify(board));
    } catch {
      // Not remembered; it still holds for this session.
    }
  }, [zoom, board]);

  return (
    <div className="w-full">
      {/*
        The heading keeps a readable measure; the calendar itself gets the
        whole screen, for the same reason the lesson plan does — a month of
        seven columns is what wants the room.
      */}
      <div className="mx-auto max-w-6xl">
        {/*
          One line: the date, then only what gets pressed daily. Everything
          else — the overlays, the categories, the sizing — is behind the
          gear, because a header is not a place to keep a settings panel.
        */}
        <header className="page-header flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <h1 className="text-2xl">{heading}</h1>

          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex overflow-hidden rounded-lg border border-sand">
              {CALENDAR_VIEWS.map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={[
                    'px-2.5 py-1 text-xs capitalize transition-colors',
                    view === v ? 'accent-fill' : 'bg-white text-walnut hover:bg-linen',
                  ].join(' ')}
                >
                  {v}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => move(-1)}
                className="btn-secondary px-2 py-1 text-xs"
                aria-label="Previous"
              >
                ←
              </button>
              <button
                onClick={() => setAnchor(today)}
                className="btn-secondary px-2.5 py-1 text-xs"
              >
                Today
              </button>
              <button
                onClick={() => move(1)}
                className="btn-secondary px-2 py-1 text-xs"
                aria-label="Next"
              >
                →
              </button>
            </div>

            <button
              onClick={() => setCreatingOn(anchor)}
              className="btn-primary px-2.5 py-1 text-xs"
            >
              Add event
            </button>

            <HeaderMenu label="Calendar settings">
              {(close) => (
                <>
                  <MenuSection title="Show on the calendar">
                    <MenuToggle checked={showHolidays} onChange={setShowHolidays}>
                      Federal holidays
                    </MenuToggle>
                    <MenuToggle checked={showDistrict} onChange={setShowDistrict}>
                      {DISTRICT_NAME} calendar
                    </MenuToggle>
                  </MenuSection>

                  <MenuSection title="Size">
                    <BoardControls
                      zoom={zoom}
                      onZoom={setZoom}
                      onFit={() => setBoard({ width: null, height: null })}
                    />
                  </MenuSection>

                  <MenuSection title="Events">
                    <button
                      className="btn-soft w-full"
                      onClick={() => {
                        setManagingCategories(true);
                        close();
                      }}
                    >
                      Manage categories
                    </button>
                  </MenuSection>
                </>
              )}
            </HeaderMenu>
          </div>
        </header>
      </div>

      {/* A month grid is wider than it is tall; the rest of the app is not. */}
      <div className="print-landscape">
        <ResizableBoard zoom={zoom} size={board} onResize={setBoard} loading={isLoading}>
          {view === 'month' && (
            <div className="overflow-hidden rounded-card border border-sand/70 bg-white shadow-soft">
              <div className="accent-head grid grid-cols-7 border-b border-sand">
                {DOW.map((d) => (
                  <div key={d} className="px-2 py-1.5 text-center text-[11px] text-walnut">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {days.map((d) => {
                  const inMonth = parseDateKey(d).getMonth() === anchorDate.getMonth();
                  const list = byDate.get(d) ?? [];
                  return (
                    <div
                      key={d}
                      onClick={() => setCreatingOn(d)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(ke) => {
                        if (ke.key === 'Enter') setCreatingOn(d);
                      }}
                      className={[
                        'min-h-[92px] cursor-pointer border-b border-r border-sand/60 p-1 transition-colors hover:bg-linen/50',
                        inMonth ? '' : 'bg-cream/60',
                        d === today ? 'bg-terracotta/[0.07]' : '',
                      ].join(' ')}
                    >
                      <div
                        className={[
                          'mb-1 px-1 text-[11px]',
                          d === today
                            ? 'font-medium text-terracotta'
                            : inMonth
                              ? 'text-walnut'
                              : 'text-stone',
                        ].join(' ')}
                      >
                        {parseDateKey(d).getDate()}
                      </div>
                      <div className="space-y-0.5">
                        {(holidaysByDate.get(d) ?? []).map((h) => (
                          <HolidayChip key={h.name} h={h} />
                        ))}
                        {(districtByDate.get(d) ?? []).map((dm) => (
                          <DistrictChip key={dm.name} d={dm} />
                        ))}
                        {list.slice(0, 3).map((e) => (
                          <EventChip
                            key={e.id}
                            e={e}
                            categories={allCategories}
                            onClick={() => setEditing(e)}
                          />
                        ))}
                        {list.length > 3 && (
                          <button
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setAnchor(d);
                              setView('day');
                            }}
                            className="px-1.5 text-[10px] text-stone hover:text-terracotta"
                          >
                            +{list.length - 3} more
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === 'week' && (
            <div className="grid gap-3 md:grid-cols-4 xl:grid-cols-7">
              {days.map((d) => {
                const list = byDate.get(d) ?? [];
                return (
                  <section
                    key={d}
                    onClick={() => setCreatingOn(d)}
                    className={[
                      'cursor-pointer rounded-card border bg-white p-3 shadow-soft transition-colors hover:border-clay',
                      d === today ? 'border-terracotta/50' : 'border-sand/70',
                    ].join(' ')}
                  >
                    <div className="mb-2 flex items-baseline justify-between">
                      <h2 className={['text-sm', d === today ? 'text-terracotta' : ''].join(' ')}>
                        {parseDateKey(d).toLocaleDateString(undefined, { weekday: 'short' })}{' '}
                        <span className="text-[11px] text-stone">{parseDateKey(d).getDate()}</span>
                      </h2>
                      <button
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setCreatingOn(d);
                        }}
                        className="btn-soft"
                        aria-label={`Add an event on ${d}`}
                      >
                        +
                      </button>
                    </div>
                    <div className="space-y-1">
                      {(holidaysByDate.get(d) ?? []).map((h) => (
                        <HolidayChip key={h.name} h={h} />
                      ))}
                      {(districtByDate.get(d) ?? []).map((dm) => (
                        <DistrictChip key={dm.name} d={dm} />
                      ))}
                      {list.length === 0 &&
                        (holidaysByDate.get(d) ?? []).length === 0 &&
                        (districtByDate.get(d) ?? []).length === 0 && (
                          <p className="text-xs text-stone">Nothing</p>
                        )}
                      {list.map((e) => (
                        <EventChip
                          key={e.id}
                          e={e}
                          categories={allCategories}
                          onClick={() => setEditing(e)}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}

          {view === 'day' && (
            <div className="card max-w-2xl">
              {(holidaysByDate.get(anchor) ?? []).map((h) => (
                <div
                  key={h.name}
                  className="mb-3 rounded-lg bg-rose/10 px-3 py-2 text-sm text-rust"
                >
                  {h.name}
                </div>
              ))}
              {(districtByDate.get(anchor) ?? []).map((dm) => (
                <div
                  key={dm.name}
                  className="mb-3 rounded-lg px-3 py-2 text-sm"
                  style={{ backgroundColor: `${dm.color}22`, color: dm.color }}
                >
                  {dm.name}
                  <span className="ml-1.5 text-xs opacity-70">{DISTRICT_NAME}</span>
                </div>
              ))}
              {(byDate.get(anchor) ?? []).length === 0 ? (
                <button
                  onClick={() => setCreatingOn(anchor)}
                  className="w-full rounded-lg border border-dashed border-sand py-8 text-sm text-stone hover:border-clay hover:bg-linen/50"
                >
                  Nothing scheduled — click to add an event
                </button>
              ) : (
                <ul className="space-y-2">
                  {(byDate.get(anchor) ?? []).map((e) => (
                    <li key={e.id}>
                      <button
                        onClick={() => setEditing(e)}
                        className="flex w-full items-start gap-3 rounded-lg border-l-[3px] px-3 py-2 text-left hover:bg-linen"
                        style={{ borderLeftColor: eventColor(e, allCategories) }}
                      >
                        <span className="w-24 shrink-0 text-xs text-stone">
                          {e.allDay
                            ? 'All day'
                            : `${minutesToLabel(e.startMinutes)} – ${minutesToLabel(e.endMinutes)}`}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-espresso">{e.title}</span>
                          {e.location && (
                            <span className="block text-xs text-stone">{e.location}</span>
                          )}
                          {e.description && (
                            <span className="mt-0.5 block text-xs text-walnut">
                              {e.description}
                            </span>
                          )}
                        </span>
                        {e.isRecurring && <span className="text-[11px] text-clay">↻</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </ResizableBoard>
      </div>

      {(editing || creatingOn) && (
        <EventEditor
          instance={editing}
          date={creatingOn ?? anchor}
          onManageCategories={() => setManagingCategories(true)}
          onClose={() => {
            setEditing(null);
            setCreatingOn(null);
          }}
        />
      )}

      {managingCategories && <CategoryManager onClose={() => setManagingCategories(false)} />}
    </div>
  );
}
